import { collection, doc, onSnapshot, query, setDoc, where } from "firebase/firestore";
import { db } from "../../firebase/config";
import { buildOrderOrigin } from "../../utils/pos/orderOrigin";

export const TABLE_STATUSES = [
  "available",
  "seated",
  "ordering",
  "inService",
  "paymentPending",
  "dirty",
  "maintenance",
];

export const TABLE_STATUS_OPTIONS = [
  { id: "available", labelKey: "tableStatusAvailable", faceKey: "", defaultLabel: "Available", defaultFace: "" },
  { id: "seated", labelKey: "tableStatusSeated", faceKey: "tableFaceSeated", defaultLabel: "Seated", defaultFace: "Seated" },
  { id: "ordering", labelKey: "tableStatusOrdering", faceKey: "tableFaceOrdering", defaultLabel: "Ordering", defaultFace: "Ordering" },
  { id: "inService", labelKey: "tableStatusInService", faceKey: "tableFaceService", defaultLabel: "In service", defaultFace: "Service" },
  { id: "paymentPending", labelKey: "tableStatusPayment", faceKey: "tableFacePayment", defaultLabel: "Payment pending", defaultFace: "Payment" },
  { id: "dirty", labelKey: "tableStatusDirty", faceKey: "tableFaceDirty", defaultLabel: "Dirty", defaultFace: "Dirty" },
  { id: "maintenance", labelKey: "tableStatusMaintenance", faceKey: "tableFaceMaintenance", defaultLabel: "In maintenance", defaultFace: "Maintenance" },
];

const SERVICE_COLLECTION = "tableService";
const serviceMemory = new Map();
const writeChain = new Map();
const localListeners = new Set();
const paymentModeMemory = new Map();

export function rememberPaymentTableStatus(storeId, mode) {
  if (!storeId || (mode !== "available" && mode !== "dirty")) return;
  paymentModeMemory.set(String(storeId), mode);
}

async function readAfterPaymentMode(storeId) {
  const remembered = paymentModeMemory.get(String(storeId || ""));
  if (remembered === "available" || remembered === "dirty") return remembered;
  try {
    const storeDB = (await import("../../utils/indexedDB/storeDB")).default;
    await storeDB.init();
    const saved = await storeDB.getStoreSetting(storeId, "tableStatusAfterPayment");
    if (saved === "available" || saved === "dirty") return saved;
  } catch (error) {
    // The cached store document is the next source.
  }
  try {
    const store = await (await localDb()).getMasterData("stores", storeId);
    if (store?.tableStatusAfterPayment === "available" || store?.tableStatusAfterPayment === "dirty") {
      return store.tableStatusAfterPayment;
    }
  } catch (error) {
    // A missing setting keeps Dirty after payment.
  }
  return "dirty";
}

export function statusFieldKey(tableId) {
  return String(tableId || "").replace(/[./~*[\]\s]/g, "_");
}

export function serviceDocId(storeId, tableId) {
  return `${storeId}_${statusFieldKey(tableId)}`;
}

function emptyServiceMap() {
  return {};
}

export function readTableService(storeId) {
  return serviceMemory.get(storeId) || emptyServiceMap();
}

export function normalizeStatusRecord(value) {
  if (!value) return { status: "", sessionId: "", at: 0 };
  if (typeof value === "string") return { status: value, sessionId: "", at: 0 };
  return {
    status: String(value.status || ""),
    sessionId: String(value.sessionId || ""),
    at: Number(value.at) || 0,
  };
}

export function chooseStatusRecord(override, cached, remote, session) {
  const list = [override, cached, remote].map(normalizeStatusRecord).filter((item) => item.status);
  if (session?.tableServiceStatus) {
    list.push(normalizeStatusRecord({
      status: session.tableServiceStatus,
      sessionId: session.tableServiceSessionId || "",
      at: session.tableServiceStatusAt,
    }));
  }
  list.sort((left, right) => (Number(left.at) || 0) - (Number(right.at) || 0));
  return list.length ? list[list.length - 1] : null;
}

export function displayTableStatus({ stored, sessionOpen }) {
  const status = normalizeStatusRecord(stored).status;
  if (status === "available") return sessionOpen ? "seated" : "available";
  if (TABLE_STATUSES.includes(status)) return status;
  return sessionOpen ? "seated" : "available";
}

export function orderSignal({ items, printHistory, afterPayment }) {
  const history = Array.isArray(printHistory) ? printHistory : [];
  if (history.some((entry) => String(entry?.type || "") === "closing")) {
    return afterPayment === "available" ? "available" : "dirty";
  }
  const bill = history.some((entry) => {
    const type = String(entry?.type || "");
    return type === "temporary" || type === "temporary revised";
  });
  if (bill) return "paymentPending";
  const submitted = (items || []).some((item) => {
    const printed = Number(item?.printedQuantity) || 0;
    const sent = Number(item?.splitInfo?.kitchenSent) || 0;
    return printed > 0 || sent > 0;
  }) || history.some((entry) => String(entry?.type || "") === "kitchen" || entry?.formImpr === "NON");
  return submitted ? "inService" : "";
}

export function nextAutomaticStatus(current, signal) {
  if (signal === "available") {
    if (current === "maintenance" || current === "available") return "";
    return "available";
  }
  if (signal !== "inService" && signal !== "paymentPending" && signal !== "dirty") return "";
  if (current === "maintenance" || current === "dirty" || current === signal) return "";
  if (signal === "dirty") return "dirty";
  if (current === "paymentPending") return "";
  if (signal === "paymentPending") return "paymentPending";
  if (current === "inService") return "";
  return "inService";
}

function normalizeLockRecord(value) {
  if (!value || typeof value !== "object") return null;
  if (value.locked !== true && value.locked !== false) return null;
  return {
    locked: value.locked === true,
    by: String(value.by || ""),
    byId: String(value.byId || ""),
    until: value.until ?? null,
    at: Number(value.at) || 0,
  };
}

export function chooseLockRecord(override, cached, remote, session) {
  const list = [override, cached, remote].map(normalizeLockRecord).filter((item) => item && item.at);
  if (!list.length && session && (session.tableLocked === true || session.tableLocked === false)) {
    return {
      locked: session.tableLocked === true,
      by: String(session.tableLockedBy || ""),
      byId: String(session.tableLockedById || ""),
      until: session.tableLockedUntil ?? null,
      at: 0,
    };
  }
  list.sort((left, right) => left.at - right.at);
  return list.length ? list[list.length - 1] : null;
}

export function canLockTable(status) {
  return status === "available" || status === "maintenance";
}

export function sameLockOwner(lockedBy, staffName, lockedById, staffId) {
  const ownerId = String(lockedById || "").trim();
  const personId = String(staffId || "").trim();
  const owner = String(lockedBy || "").trim().toLowerCase();
  const staff = String(staffName || "").trim().toLowerCase();
  const sameId = ownerId !== "" && personId !== "" && ownerId === personId;
  const sameName = owner !== "" && staff !== "" && owner === staff;
  return sameId || sameName;
}

const OCCUPIED_AFTER_PAYMENT = new Set(["seated", "ordering", "inService", "paymentPending"]);

function millisOf(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) return value.getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

export function tableWasPaid(orders, tableName, statusAt) {
  const name = String(tableName || "");
  if (!name) return false;
  const mark = Number(statusAt) || 0;
  return (orders || []).some((order) => {
    const status = order?.status;
    const paid = status === 1 || status === 2 || status === "1" || status === "2";
    if (!paid) return false;
    const label = String(order.tableName || order.orderNumber || "");
    if (label !== name) return false;
    const when = Math.max(millisOf(order.paidAt), millisOf(order.updated), millisOf(order.created));
    if (!when) return mark === 0;
    return when + 120000 >= mark;
  });
}

export function shouldMarkDirtyAfterPayment({ stored, orders, tableName, sessionOpen }) {
  const record = normalizeStatusRecord(stored);
  if (sessionOpen) return false;
  if (!OCCUPIED_AFTER_PAYMENT.has(record.status)) return false;
  return tableWasPaid(orders, tableName, record.at);
}

function normalizeServiceRecord(value, storeId, tableId) {
  const field = statusFieldKey(tableId || value?.tableId || value?.tableKey);
  return {
    id: value?.id || serviceDocId(storeId, field),
    storeId: String(storeId || value?.storeId || ""),
    tableId: String(tableId || value?.tableId || ""),
    tableKey: field,
    stationId: String(value?.stationId || ""),
    status: TABLE_STATUSES.includes(value?.status) ? value.status : "",
    sessionId: String(value?.sessionId || ""),
    locked: value?.locked === true,
    by: value?.locked === true ? String(value?.by || "") : "",
    byId: value?.locked === true ? String(value?.byId || "") : "",
    until: value?.locked === true ? (value?.until ?? null) : null,
    at: Number(value?.at) || 0,
  };
}

function legacyMaps(storeId) {
  const read = (key) => {
    if (typeof localStorage === "undefined") return {};
    try {
      const parsed = JSON.parse(localStorage.getItem(key) || "{}");
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      return {};
    }
  };
  return {
    status: read(`nuage-table-status:${storeId}`),
    lock: read(`nuage-table-lock:${storeId}`),
  };
}

async function localDb() {
  const offlineDB = (await import("../../utils/indexedDB/offlineDB")).default;
  await offlineDB.init();
  return offlineDB;
}

function rememberService(record) {
  const map = { ...(serviceMemory.get(record.storeId) || emptyServiceMap()) };
  map[record.tableKey] = record;
  serviceMemory.set(record.storeId, map);
  localListeners.forEach((listener) => listener(record.storeId, map));
  return map;
}

export function subscribeLocalTableService(listener) {
  localListeners.add(listener);
  return () => localListeners.delete(listener);
}

export async function applyCartStatus(sessionId) {
  const cartDB = (await import("../../utils/indexedDB/cartDB")).default;
  await cartDB.init();
  const session = await cartDB.getSessionObject(sessionId);
  if (!session?.storeId || !session?.tableName) return null;
  const items = await cartDB.getCartItems(sessionId);
  const afterPayment = await readAfterPaymentMode(session.storeId);
  const signal = orderSignal({ items, printHistory: session.printHistory, afterPayment });
  if (!signal) return null;
  let tableId = session.tableId || "";
  if (!tableId) {
    const offlineDB = await localDb();
    const store = await offlineDB.getMasterData("stores", session.storeId);
    const match = (store?.tables || []).find((table) => table?.name === session.tableName);
    tableId = match?.id || session.tableName;
  }
  const field = statusFieldKey(tableId);
  const stored = chooseStatusRecord(null, (serviceMemory.get(session.storeId) || emptyServiceMap())[field], null, session);
  const next = nextAutomaticStatus(normalizeStatusRecord(stored).status, signal);
  if (!next) return null;
  return saveTableServiceStatus({
    storeId: session.storeId,
    table: { id: tableId, name: session.tableName },
    session,
    status: next,
  });
}

async function syncServiceRecord(record) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;
  try {
    await setDoc(doc(db, SERVICE_COLLECTION, record.id), record, { merge: true });
    const offlineDB = await localDb();
    await offlineDB.markSynced(SERVICE_COLLECTION, record.id);
  } catch (error) {
    console.warn("[tableServiceStatus] Table service record was not synced", error);
  }
}

function enqueueServiceWrite(recordId, job) {
  const previous = writeChain.get(recordId) || Promise.resolve();
  const next = previous.then(job, job);
  writeChain.set(recordId, next);
  return next;
}

async function writeServiceRecord(storeId, tableId, patch) {
  const field = statusFieldKey(tableId);
  const recordId = serviceDocId(storeId, field);
  return enqueueServiceWrite(recordId, async () => {
    const current = (serviceMemory.get(storeId) || emptyServiceMap())[field] || {};
    const origin = buildOrderOrigin({ id: storeId });
    const record = normalizeServiceRecord({
      ...current,
      ...patch,
      stationId: origin.stationId || current.stationId || "",
      at: Date.now(),
    }, storeId, tableId);
    rememberService(record);
    const offlineDB = await localDb();
    await offlineDB.saveDocument(SERVICE_COLLECTION, record.id, record, false);
    syncServiceRecord(record);
    return record;
  });
}

export async function loadTableService(storeId) {
  if (!storeId) return emptyServiceMap();
  const offlineDB = await localDb();
  const docs = await offlineDB.getCollection(SERVICE_COLLECTION);
  const map = {};
  docs.forEach((item) => {
    if (item.storeId !== storeId) return;
    const record = normalizeServiceRecord(item, storeId, item.tableId || item.tableKey);
    const previous = map[record.tableKey];
    if (!previous || record.at >= previous.at) map[record.tableKey] = record;
  });
  if (!Object.keys(map).length) {
    const legacy = legacyMaps(storeId);
    const keys = new Set([...Object.keys(legacy.status), ...Object.keys(legacy.lock)]);
    keys.forEach((key) => {
      const status = legacy.status[key] || {};
      const lock = legacy.lock[key] || {};
      const at = Math.max(Number(status.at) || 0, Number(lock.at) || 0);
      if (!at && !status.status && lock.locked !== true) return;
      map[key] = normalizeServiceRecord({
        tableId: key,
        tableKey: key,
        status: status.status,
        sessionId: status.sessionId,
        locked: lock.locked === true,
        by: lock.by,
        byId: lock.byId,
        until: lock.until,
        at: at || Date.now(),
      }, storeId, key);
    });
    await Promise.all(Object.values(map).map(async (record) => {
      await offlineDB.saveDocument(SERVICE_COLLECTION, record.id, record, false);
      syncServiceRecord(record);
    }));
  }
  const current = serviceMemory.get(storeId) || emptyServiceMap();
  Object.entries(current).forEach(([key, record]) => {
    if (!map[key] || record.at > map[key].at) map[key] = record;
  });
  serviceMemory.set(storeId, map);
  const pending = await offlineDB.getUnsyncedDocuments(SERVICE_COLLECTION);
  pending.forEach((item) => {
    if (item?.data?.storeId !== storeId) return;
    syncServiceRecord(normalizeServiceRecord(item.data, storeId, item.data.tableId));
  });
  return map;
}

export function subscribeTableService(storeId, onMap) {
  if (!storeId) return () => {};
  const serviceQuery = query(collection(db, SERVICE_COLLECTION), where("storeId", "==", storeId));
  return onSnapshot(serviceQuery, (snapshot) => {
    let changed = false;
    snapshot.docChanges().forEach((change) => {
      if (change.type === "removed") return;
      const incoming = normalizeServiceRecord({ ...change.doc.data(), id: change.doc.id }, storeId, change.doc.data().tableId);
      const current = (serviceMemory.get(storeId) || emptyServiceMap())[incoming.tableKey];
      if (current && current.at > incoming.at) return;
      rememberService(incoming);
      changed = true;
      localDb().then((offlineDB) => offlineDB.saveDocument(SERVICE_COLLECTION, incoming.id, incoming, true)).catch(() => {});
    });
    if (changed) onMap?.({ ...(serviceMemory.get(storeId) || emptyServiceMap()) });
  }, (error) => {
    console.warn("[tableServiceStatus] Table service sync is waiting", error);
  });
}

export async function adoptStoreService(storeId, statusMap, lockMap) {
  if (!storeId) return emptyServiceMap();
  const status = statusMap && typeof statusMap === "object" ? statusMap : {};
  const locks = lockMap && typeof lockMap === "object" ? lockMap : {};
  const keys = new Set([...Object.keys(status), ...Object.keys(locks)]);
  const offlineDB = keys.size ? await localDb() : null;
  for (const key of keys) {
    const statusRecord = status[key] && typeof status[key] === "object" ? status[key] : {};
    const lockRecord = locks[key] && typeof locks[key] === "object" ? locks[key] : {};
    const at = Math.max(Number(statusRecord.at) || 0, Number(lockRecord.at) || 0);
    if (!at && !statusRecord.status && lockRecord.locked !== true) continue;
    const field = statusFieldKey(key);
    const existing = (serviceMemory.get(storeId) || emptyServiceMap())[field];
    if (existing && existing.at >= at) continue;
    const record = normalizeServiceRecord({
      tableId: key,
      tableKey: field,
      status: statusRecord.status,
      sessionId: statusRecord.sessionId,
      locked: lockRecord.locked === true,
      by: lockRecord.by,
      byId: lockRecord.byId,
      until: lockRecord.until,
      at: at || Date.now(),
    }, storeId, key);
    rememberService(record);
    await offlineDB.saveDocument(SERVICE_COLLECTION, record.id, record, false);
    syncServiceRecord(record);
  }
  return { ...(serviceMemory.get(storeId) || emptyServiceMap()) };
}

export async function saveTableLock({ storeId, table, locked, by, byId, until }) {
  const tableId = String(table?.id || table?.name || "").trim();
  if (!storeId || !tableId) return null;
  return writeServiceRecord(storeId, tableId, {
    locked: locked === true,
    by: locked ? String(by || "") : "",
    byId: locked ? String(byId || "") : "",
    until: locked ? (until ?? null) : null,
  });
}

export async function markClosedTablesDirty({ storeId, entries, orders }) {
  if (!storeId || !Array.isArray(entries) || !entries.length) return;
  const map = serviceMemory.get(storeId) || emptyServiceMap();
  await Promise.all(entries.map(async ({ table, session, sessionOpen }) => {
    const field = statusFieldKey(table?.id || table?.name);
    const stored = chooseStatusRecord(null, map[field], null, session);
    if (!shouldMarkDirtyAfterPayment({
      stored,
      orders,
      tableName: table?.name,
      sessionOpen: sessionOpen === true,
    })) return;
    await saveTableServiceStatus({ storeId, table, session: null, status: "dirty" });
  }));
}

export async function saveTableServiceStatus({ storeId, table, session, status }) {
  const tableId = String(table?.id || table?.name || "").trim();
  if (!storeId || !tableId || !TABLE_STATUSES.includes(status)) return null;
  return writeServiceRecord(storeId, tableId, {
    status,
    sessionId: session?.id ? String(session.id) : "",
  });
}
